/**
 * AnchorPills — Tier 0 module-local primitive per ADR
 * `frontend-0004` (`docs/adr/frontend-0004-ui-kit-doctrine.md`). Lives
 * in this module's `_components/` folder because Tier 3 promotion to
 * `@sparta/ui-kit` requires a real cross-slice import in code, which
 * does not exist on this branch. Stays at Tier 0 until that reactive
 * trigger fires.
 *
 * Visual: a single cylindrical pill containing the section labels
 * (with optional counts), with a sliding active background that
 * animates between selections. The sliding background reads
 * `offsetLeft` / `offsetWidth` from the active pill so it always
 * tracks the active label width.
 *
 * Behavior:
 *  - Click a pill → calls `onChange(id)`.
 *  - ArrowRight / ArrowLeft → cycles the active pill with wrap-around.
 *  - Home / End → jumps to first / last.
 *  - `aria-current="location"` marks the active pill (WAI-ARIA
 *    in-page navigation semantics); the container is a `<nav>` with
 *    the accessible label `aria-label`.
 *  - The sliding background div carries `aria-hidden` — it is pure
 *    visual chrome.
 *
 * Geometry resync — the sliding bar must stay glued to the active
 * pill even when the active id does not change. Cases the primitive
 * handles:
 *  1. `active` changes → `useLayoutEffect` dep hits, remeasure.
 *  2. `sections` array reference changes (label / count edit, new
 *     section inserted before the active one, etc.) → `useLayoutEffect`
 *     dep hits, remeasure.
 *  3. A pill's geometry changes without a re-render (text reflow on
 *     font load, viewport resize, sibling count chip appearing) →
 *     `ResizeObserver` on each pill fires `remeasure`.
 * Case 3 is the bug Codex Step-2-Round-1 flagged: the previous
 * implementation depended only on `[active, sections.length]`, so
 * same-id geometry drift left the bar pointing at stale measurements.
 *
 * Note re jsdom: `offsetLeft` / `offsetWidth` are 0 in jsdom by default,
 * so the bar style starts at zero in unit tests. Tests mock those
 * properties on the pill elements and invoke the captured
 * `ResizeObserver` callback (or rerender with new `sections`) to drive
 * the resync path explicitly.
 */
import * as React from 'react';

export interface AnchorPillsSection {
  readonly id: string;
  readonly label: string;
  /** Optional count chip rendered next to the label (e.g. unread count). */
  readonly count?: number;
}

export interface AnchorPillsProps {
  readonly sections: ReadonlyArray<AnchorPillsSection>;
  readonly active: string;
  readonly onChange: (id: string) => void;
  /** Accessible label for the nav container (e.g. "User detail sections"). */
  readonly 'aria-label': string;
}

export function AnchorPills({
  sections,
  active,
  onChange,
  'aria-label': ariaLabel,
}: AnchorPillsProps): React.ReactElement {
  const refs = React.useRef<Record<string, HTMLButtonElement | null>>({});
  const [bar, setBar] = React.useState<{ left: number; width: number }>({
    left: 0,
    width: 0,
  });

  React.useLayoutEffect(() => {
    function remeasure(): void {
      const el = refs.current[active];
      if (el !== null && el !== undefined) {
        setBar({ left: el.offsetLeft, width: el.offsetWidth });
      }
    }
    remeasure();

    // ResizeObserver catches geometry changes that do NOT change
    // `active` or the `sections` reference — text reflow on font
    // load, viewport resize, a sibling pill's count chip appearing /
    // disappearing. Observing every pill (not just the active one)
    // is intentional: when a non-active pill resizes, the active
    // pill's `offsetLeft` shifts even though its own size is unchanged,
    // so we still need to remeasure.
    if (typeof ResizeObserver === 'undefined') {
      return;
    }
    const ro = new ResizeObserver(remeasure);
    for (const el of Object.values(refs.current)) {
      if (el !== null && el !== undefined) {
        ro.observe(el);
      }
    }
    return () => {
      ro.disconnect();
    };
  }, [active, sections]);

  function cycleBy(delta: number): void {
    if (sections.length === 0) return;
    const idx = sections.findIndex((s) => s.id === active);
    const next = (idx + delta + sections.length) % sections.length;
    const target = sections[next];
    if (target !== undefined) onChange(target.id);
  }

  function handleKeyDown(e: React.KeyboardEvent<HTMLButtonElement>): void {
    if (e.key === 'ArrowRight') {
      e.preventDefault();
      cycleBy(1);
    } else if (e.key === 'ArrowLeft') {
      e.preventDefault();
      cycleBy(-1);
    } else if (e.key === 'Home') {
      e.preventDefault();
      const first = sections[0];
      if (first !== undefined) onChange(first.id);
    } else if (e.key === 'End') {
      e.preventDefault();
      const last = sections[sections.length - 1];
      if (last !== undefined) onChange(last.id);
    }
  }

  return (
    <nav
      aria-label={ariaLabel}
      className="relative inline-flex p-1 rounded-full bg-[var(--bg-surface)] border border-[var(--border-subtle)] shadow-sm"
    >
      {/* Sliding active background — pure visual chrome. */}
      <span
        aria-hidden="true"
        className="absolute top-1 rounded-full bg-brand-500 transition-[left,width] duration-300 ease-out"
        style={{
          left: bar.left,
          width: bar.width,
          height: 'calc(100% - 0.5rem)',
        }}
      />
      {sections.map((s) => {
        const isActive = s.id === active;
        return (
          // ADR frontend-0004 exemption: AnchorPills' pill is a custom
          // interactive primitive (sliding bg, keyboard cycling, count chip).
          // Composing kit Button would not match the visual or a11y
          // contract; this is the "novel interactive primitive" case the
          // lint rule allows under the trap-promotion ladder entry rule
          // per AGENTS.md.
          // eslint-disable-next-line @sparta/no-hand-rolled-button
          <button
            key={s.id}
            ref={(el) => {
              refs.current[s.id] = el;
            }}
            type="button"
            aria-current={isActive ? 'location' : undefined}
            onClick={() => onChange(s.id)}
            onKeyDown={handleKeyDown}
            className={[
              'relative z-10 inline-flex items-center gap-2 whitespace-nowrap rounded-full px-4 py-2 text-xs transition-colors',
              isActive
                ? 'font-semibold text-white'
                : 'font-medium text-[var(--fg-secondary)]',
            ].join(' ')}
          >
            <span>{s.label}</span>
            {s.count !== undefined && s.count !== 0 ? (
              <span
                className={[
                  'min-w-5 rounded-full px-2 text-[11px] font-semibold text-center',
                  isActive
                    ? 'bg-white/20 text-white'
                    : 'bg-[var(--bg-sunken)] text-[var(--fg-tertiary)]',
                ].join(' ')}
              >
                {s.count}
              </span>
            ) : null}
          </button>
        );
      })}
    </nav>
  );
}
